import React from 'react';
import NavBar from '@/components/layout/NavBar';
import Footer from '@/components/layout/Footer';
import { useLanguage } from '@/components/i18n/language-provider';
import { Separator } from '@/components/ui/separator';

const PrivacySecurity: React.FC = () => {
  const { t } = useLanguage();
  const effectiveDate = "March 7, 2025";

  return (
    <div className="flex flex-col min-h-screen">
      <NavBar />
      
      <main className="flex-grow pt-28 md:pt-32">
        <div className="container mx-auto px-6 py-12 max-w-4xl">
          <div className="mb-8 bg-white dark:bg-background p-8 rounded-lg shadow-sm">
            <h1 className="text-3xl md:text-4xl font-display font-semibold mb-4">Privacy and Security</h1>
            <p className="text-muted-foreground">Effective Date: {effectiveDate}</p>
            <Separator className="my-6" />
          </div>
          
          <div className="prose prose-sm md:prose-base max-w-none bg-white dark:bg-background p-8 rounded-lg shadow-sm">
            <section className="mb-8">
              <h2 className="text-xl md:text-2xl font-display font-medium mb-4">Overview</h2>
              <p className="mb-4">
                At Keystle, safeguarding your privacy is paramount. Our services are designed for enterprise clients; 
                when delivering these services, we may process information about you. This Privacy Policy ("Policy") 
                details how Keystle collects, uses, and discloses your information, alongside your rights and choices. 
                References to "Keystle," "we," "our," or "us" pertain to Keystle LLC. and its affiliates. "You" or 
                "your" refers to individuals interacting with us.
              </p>
              <p className="mb-4">
                By accessing or using our services, you acknowledge that you've read and understood this Policy. 
                Please review it carefully, as it applies when you use our services or products, visit our website, 
                or utilize the Keystle mobile application. Capitalized terms not defined herein have the meanings 
                assigned in our Platform Agreement.
              </p>
              <p>
                In specific contexts, we may provide additional "just-in-time" disclosures or information about our 
                data processing practices. These notices may supplement this Policy, clarify Keystle's privacy practices, 
                or offer you additional choices regarding how Keystle processes your personal information.
              </p>
            </section>
            
            <section className="mb-8">
              <h2 className="text-xl md:text-2xl font-display font-medium mb-4">1. Applicability of This Privacy Policy</h2>
              <p className="mb-4">This Policy applies to personal information we collect and process when you:</p>
              <ul className="list-disc pl-6 mb-4 space-y-2">
                <li>Visit, interact with, or use any Keystle website and/or services;</li>
                <li>Create or manage your Keystle account;</li>
                <li>Receive communications from or otherwise interact with us, including via email, phone, mail, or our branded social media pages;</li>
                <li>Register for or participate in our marketing events, training sessions, or workshops; and/or</li>
                <li>Offer or integrate your application, content, product, or services on or through Keystle's services.</li>
              </ul>
              
              <p className="mb-4">This Policy does not apply to:</p>
              <ul className="list-disc pl-6 space-y-2">
                <li>
                  <strong>Customer Data:</strong> Information you submit for processing to receive Keystle's services, such as bank account 
                  details for payroll or demographic data for benefits enrollment. Customer Data excludes account information 
                  you provide when creating or managing your Keystle account. For details on Keystle's handling of Customer Data, 
                  refer to our Data Processing Addendum.
                </li>
                <li>
                  <strong>Keystle as an Employer:</strong> Information we collect or process in our capacity as an employer, 
                  co-employer, or employer of record. For information collected during the application process, see our Candidate 
                  Privacy Policy.
                </li>
                <li>
                  <strong>Third-Party Partners and Service Providers ("Service Providers"):</strong> Products, services, websites, 
                  or content offered by third parties through integrations with Keystle's services, governed by their respective 
                  privacy policies.
                </li>
              </ul>
            </section>
            
            <section className="mb-8">
              <h2 className="text-xl md:text-2xl font-display font-medium mb-4">2. Personal Information We Collect</h2>
              <p className="mb-4">
                The personal information we collect depends on your interactions with Keystle, the choices you make, 
                the services and features you use, your location, and applicable laws. If you provide information about 
                another individual, you represent that you have the authority to do so, have delivered necessary notices, 
                and obtained all requisite rights and consents for us to process such information per this Policy.
              </p>
              
              <h3 className="text-lg md:text-xl font-display font-medium mb-3">A. Information You Provide to Keystle</h3>
              <p className="mb-4">
                Keystle's services are intended for enterprise clients and their administrators, employees, and authorized 
                users ("Authorized Users"). When applying for a Keystle Account, we may receive information about you, your 
                company, and individuals associated with your company, including but not limited to:
              </p>
              <ul className="list-disc pl-6 mb-4 space-y-2">
                <li>
                  <strong>Business Contact Information:</strong> Such as your name, address, phone number, email address, 
                  employer, and job title.
                </li>
                <li>
                  <strong>Account Information:</strong> Including usernames, credit card details, bank account numbers, 
                  VAT numbers, other tax identifiers, and authentication credentials.
                </li>
              </ul>
              
              <h3 className="text-lg md:text-xl font-display font-medium mb-3">B. Information Collected Automatically</h3>
              <p className="mb-4">
                When you interact with our services, we may automatically collect certain information about your device 
                and usage, including:
              </p>
              <ul className="list-disc pl-6 space-y-2">
                <li>
                  <strong>Device and Usage Information:</strong> Such as IP address, browser type, internet service provider, 
                  referring/exit pages, files viewed on our site (e.g., HTML pages, graphics), operating system, date/time stamps, 
                  and clickstream data to analyze trends in aggregate and administer the site.
                </li>
                <li>
                  <strong>Cookies and Similar Technologies:</strong> We may use cookies, beacons, tags, and scripts to collect 
                  information. You can control the use of cookies at the individual browser level; however, disabling cookies 
                  may limit certain features or functions on our services or websites.
                </li>
              </ul>
            </section>
            
            <section className="mb-8">
              <h2 className="text-xl md:text-2xl font-display font-medium mb-4">3. How We Use Your Personal Information</h2>
              <p className="mb-4">We use the personal information we collect for the following purposes:</p>
              <ul className="list-disc pl-6 space-y-2">
                <li>
                  <strong>Provide and Improve Our Services:</strong> To process transactions, manage accounts, and offer 
                  customer support.
                </li>
                <li>
                  <strong>Communicate with You:</strong> To send administrative or account-related communications, such as 
                  security updates or changes to our terms.
                </li>
                <li>
                  <strong>Marketing and Promotions:</strong> To send marketing communications, as permitted by law, informing 
                  you about our services, features, or events.
                </li>
                <li>
                  <strong>Legal Compliance and Security:</strong> To comply with legal obligations, resolve disputes, and 
                  enforce our agreements.
                </li>
              </ul>
            </section>
            
            <section className="mb-8">
              <h2 className="text-xl md:text-2xl font-display font-medium mb-4">4. How We Share Your Personal Information</h2>
              <p className="mb-4">We may share your personal information in the following ways:</p>
              <ul className="list-disc pl-6 space-y-2">
                <li>
                  <strong>With Service Providers:</strong> With third-party vendors performing services on our behalf, such as 
                  payment processing, data analysis, and hosting services.
                </li>
                <li>
                  <strong>With Business Partners:</strong> With trusted partners to provide joint services or offers that may 
                  interest you.
                </li>
                <li>
                  <strong>Legal Compliance:</strong> To comply with applicable laws, regulations, legal processes, or 
                  governmental requests.
                </li>
              </ul>
            </section>
            
            <section className="mb-8">
              <h2 className="text-xl md:text-2xl font-display font-medium mb-4">5. Your Rights and Choices</h2>
              <p className="mb-4">
                Depending on your location and subject to applicable laws, you may have the following rights regarding 
                your personal information:
              </p>
              <ul className="list-disc pl-6 space-y-2">
                <li>
                  <strong>Access and Update:</strong> You can access and update your personal information by logging into 
                  your account or contacting us directly.
                </li>
                <li>
                  <strong>Opt-Out of Marketing Communications:</strong> You can opt out of receiving marketing communications 
                  by following the unsubscribe instructions in those messages or contacting us.
                </li>
                <li>
                  <strong>Data Deletion:</strong> You can request the deletion of your personal information, subject to 
                  certain exceptions provided by law.
                </li>
              </ul>
            </section>
            
            <section className="mb-8">
              <h2 className="text-xl md:text-2xl font-display font-medium mb-4">6. Security of Your Personal Information</h2>
              <p>
                Keystle employs a comprehensive, written information security program containing industry-standard administrative, 
                technical, and physical safeguards designed to prevent the loss, theft, unauthorized access, use, disclosure, 
                or alteration of personal data. However, no security system is perfect, and due to the inherent nature of the 
                internet, we cannot guarantee that data, including personal data, is absolutely safe from intrusion or other 
                unauthorized access by others. You are responsible for protecting your password(s) and other authentication 
                factors, as well as maintaining the security of your devices.
              </p>
            </section>
            
            <section className="mb-8">
              <h2 className="text-xl md:text-2xl font-display font-medium mb-4">7. Changes to This Privacy Policy</h2>
              <p>
                We may update this Policy from time to time to reflect changes in our practices, technologies, legal 
                requirements, and other factors. When we make changes, we will update the "Effective Date" at the top 
                of the Policy and provide notice as required by applicable law.
              </p>
            </section>
            
            <section>
              <h2 className="text-xl md:text-2xl font-display font-medium mb-4">8. Contact Us</h2>
              <p className="mb-4">If you have questions or concerns about this Policy or our privacy practices, please contact us at:</p>
              <div className="p-4 bg-muted rounded-md">
                <p>Keystle LLC.</p>
                <p>175, Fox Run Rd</p>
                <p>Bolton, Massachusetts</p>
                <p><a href="mailto:privacy@keystle.com" className="text-primary hover:underline">privacy@keystle.com</a></p>
              </div>
            </section>
          </div>
        </div>
      </main>
      
      <Footer />
    </div>
  );
};

export default PrivacySecurity;
